import { FC, ReactNode, useMemo } from 'react';
import { twJoin } from 'tailwind-merge';
import { DashboardCard } from '../../shared/DashboardCard';

export type DashboardCardGridItem = {
  /**
   * The card element to render
   */
  card: ReactNode;
  /**
   * Whether the card should take double width (2 columns) on mobile
   */
  flexMobile?: boolean;
  /**
   * Whether the card should take double width (2 columns) on tablet and desktop
   */
  flexTabletAndDesktop?: boolean;
};

export type DashboardCardGridProps = {
  /**
   * Array of card items to display in the grid
   */
  items: DashboardCardGridItem[];
  /**
   * Whether to show loading skeleton state
   */
  fallback?: boolean;
  /**
   * Whether to center cards within the grid
   */
  centered?: boolean;
  /**
   * Additional CSS classes
   */
  className?: string;
};

/**
 * Calculate optimal number of columns to maximize cards in the last row
 * Takes into account flex cards which take 2 column spans
 *
 * Strategy: Choose the column count that leaves the MOST cards in the last row
 * (avoiding single orphan cards)
 *
 * Examples:
 * - 13 cards with 4 cols = 1 rest card (bad)
 * - 13 cards with 5 cols = 3 rest cards (good) ✓
 * - 7 cards with 5 cols = 2 rest cards (bad)
 * - 7 cards with 4 cols = 3 rest cards (good) ✓
 */
const calculateOptimalColumns = (
  items: DashboardCardGridItem[],
  columnOptions: number[],
  breakpoint: 'mobile' | 'tabletAndDesktop',
): number => {
  // Calculate total card units (regular cards = 1, flex cards = 2)
  const totalUnits = items.reduce((sum, item) => {
    const isFlex =
      breakpoint === 'mobile' ? item.flexMobile : item.flexTabletAndDesktop;
    return sum + (isFlex ? 2 : 1);
  }, 0);

  // Find the column count that results in the MOST cards in the last row
  let bestColumns = columnOptions[0];
  let maxRestCards = totalUnits % columnOptions[0];

  for (const columns of columnOptions) {
    const restCards = totalUnits % columns;
    // If the rest is 0, all rows are full - this is perfect
    if (restCards === 0) {
      return columns;
    }
    // Otherwise, maximize the number of rest cards (more cards in the last row is better)
    // Prefer fewer columns if the rest count is the same (more compact layout)
    if (
      restCards > maxRestCards ||
      (restCards === maxRestCards && columns < bestColumns)
    ) {
      bestColumns = columns;
      maxRestCards = restCards;
    }
  }

  return bestColumns;
};

export const DashboardCardGrid: FC<DashboardCardGridProps> = ({
  items,
  fallback,
  centered,
  className,
}) => {
  const { mdColumns, xlColumns } = useMemo(() => {
    if (fallback || items.length === 0) {
      return { mdColumns: 3, xlColumns: 4 };
    }

    // Calculate total card units for tablet and desktop (regular cards = 1, flex cards = 2)
    const totalUnitsTabletAndDesktop = items.reduce(
      (sum, item) => sum + (item.flexTabletAndDesktop ? 2 : 1),
      0,
    );

    // If centered, and we have fewer than 5 card units, use compact centered layout
    // For 5+ cards, use the normal optimal column calculation to avoid orphans
    if (centered && totalUnitsTabletAndDesktop < 5) {
      // For centered layouts with few cards, use actual card count as columns
      // This creates a compact centered layout
      // Mobile: 1-2 columns based on card count
      // Tablet (md): 1-4 columns based on card count
      // Desktop (xl): 1-5 columns based on card count
      const mdColumns = Math.min(4, Math.max(1, totalUnitsTabletAndDesktop));
      const xlColumns = Math.min(5, Math.max(1, totalUnitsTabletAndDesktop));

      return { mdColumns, xlColumns };
    }

    // Special handling for 1-2 card scenarios to ensure uniform sizing (non-centered only)
    // Uses md:3 xl:4 columns so cards maintain a consistent md:1/3 xl:1/4 grid size
    if (totalUnitsTabletAndDesktop <= 2) {
      return { mdColumns: 3, xlColumns: 4 };
    }

    // Calculate optimal columns for tablet (md) and desktop (xl) breakpoints
    // Ensure columns don't exceed totalUnits (e.g., 1 flex card = 2 units, max 2 cols)
    const mdColumns = Math.min(
      totalUnitsTabletAndDesktop,
      calculateOptimalColumns(items, [3, 4], 'tabletAndDesktop'),
    );
    const xlColumns = Math.min(
      totalUnitsTabletAndDesktop,
      calculateOptimalColumns(items, [4, 5], 'tabletAndDesktop'),
    );

    return { mdColumns, xlColumns };
  }, [items, fallback, centered]);

  const gridColsClass = useMemo(() => {
    // Mobile: 1-2 columns based on calculation
    // Tablet (md): 1-4 columns based on calculation
    // Desktop (xl): 1-5 columns based on calculation
    // max-w-size is for limiting card sizes, so they don't grow endlessly
    return twJoin(
      mdColumns === 1 ? 'grid-cols-1 max-w-[320px]' : 'grid-cols-2',
      mdColumns === 1 && 'md:grid-cols-1 md:max-w-[320px]',
      mdColumns === 2 && 'md:grid-cols-2 md:max-w-[580px]',
      mdColumns === 3 && 'md:grid-cols-3 md:max-w-full',
      mdColumns === 4 && 'md:grid-cols-4 md:max-w-full',
      xlColumns === 1 && 'xl:grid-cols-1 xl:max-w-[320px]',
      xlColumns === 2 && 'xl:grid-cols-2 xl:max-w-[580px]',
      xlColumns === 3 && 'xl:grid-cols-3 xl:max-w-full',
      xlColumns === 4 && 'xl:grid-cols-4 xl:max-w-full',
      xlColumns === 5 && 'xl:grid-cols-5 xl:max-w-full',
    );
  }, [mdColumns, xlColumns]);

  if (fallback) {
    return (
      <div
        className={twJoin(
          'grid grid-flow-dense gap-4 md:gap-6',
          gridColsClass,
          centered && 'mx-auto',
          className,
        )}
      >
        {Array.from({ length: 6 }, (_, idx) => (
          <div
            key={idx}
            className={twJoin(
              idx === 2 ? 'col-span-2' : 'col-span-1',
              idx === 2 ? 'md:col-span-2' : 'md:col-span-1',
            )}
          >
            <DashboardCard
              fallback
              flexMobile={idx === 2}
              flexTabletAndDesktop={idx === 2}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      className={twJoin(
        'grid grid-flow-dense gap-4 md:gap-6',
        gridColsClass,
        centered && 'mx-auto',
        className,
      )}
    >
      {items.map((item, idx) => (
        <div
          key={idx}
          className={twJoin(
            item.flexMobile ? 'col-span-2' : 'col-span-1',
            item.flexTabletAndDesktop ? 'md:col-span-2' : 'md:col-span-1',
          )}
        >
          {item.card}
        </div>
      ))}
    </div>
  );
};
